import React, { useEffect, useRef, useState, useCallback } from "react";
import { fetchMessages, sendMessage, fetchLeagueMembers, reactToMessage } from "../lib/api";
import { AvatarChip } from "./AvatarChip";
import { useAuth } from "../context/AuthContext";

const POLL_MS = 4000;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MENTION_SOUND_PREF = "lh.mentionSound";
const REACTION_EMOJIS = ["🔥", "⚔", "🤝", "👀", "💀", "😂", "🚀", "👍", "❤"];

function buildWsUrl(leagueId) {
  // Convert https://host -> wss://host (or http -> ws for local dev).
  let base = BACKEND || "";
  if (base.startsWith("https://")) base = "wss://" + base.slice(8);
  else if (base.startsWith("http://")) base = "ws://" + base.slice(7);
  return `${base}/api/ws/leagues/${leagueId}`;
}

// Tiny <100-byte beep encoded as a base64 wav — no asset hosting needed.
const CHIME_DATA_URL =
  "data:audio/wav;base64,UklGRoQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YWAAAACAhYqMjImHhIB+fX1+gIKEhomLi4mIhYJ/fXx9foCDhomLjImHhYJ/fX1+f4GEh4qLi4mGg4B+fH1+gIKFiIqLi4mHhIF+fH1+f4GDh4mLi4qIhoN/fX1+f4GDhYiKi4uJh4SBfn1+";

function playChime() {
  try {
    if (localStorage.getItem(MENTION_SOUND_PREF) === "off") return;
    const a = new Audio(CHIME_DATA_URL);
    a.volume = 0.4;
    a.play().catch(() => { /* user-gesture gate — first message ever may fail silently */ });
  } catch (_e) { /* noop */ }
}

// Render a message body with @mentions highlighted as inline pills.
function renderBody(body, mentions, viewerId) {
  if (!mentions || mentions.length === 0) return body;
  // Match @Name (greedy alpha-num underscore, 2-40 chars). Same shape as backend.
  const re = /@([A-Za-z0-9_]{2,40})/g;
  const out = [];
  const names = mentions.map((m) => ({ ...m, _norm: (m.name || "").toLowerCase() }));
  let lastIdx = 0;
  let match;
  while ((match = re.exec(body)) !== null) {
    const token = match[1].toLowerCase();
    const hit = names.find(
      (m) => m._norm === token || m._norm.replace(/\s+/g, "") === token,
    );
    if (!hit) continue;
    if (match.index > lastIdx) out.push(body.slice(lastIdx, match.index));
    const isMe = hit.user_id === viewerId;
    out.push(
      <span
        key={`m-${match.index}`}
        data-testid={`chat-mention-${hit.user_id}`}
        className={`inline-block px-1.5 py-0.5 rounded-md mx-0.5 font-bold text-[12px] ${
          isMe
            ? "bg-[#14b8a6]/30 text-[#14b8a6] border border-[#14b8a6]/50"
            : "bg-[#14b8a6]/20 text-[#14b8a6] border border-[#14b8a6]/40"
        }`}
      >
        @{hit.name}
      </span>,
    );
    lastIdx = match.index + match[0].length;
  }
  if (lastIdx < body.length) out.push(body.slice(lastIdx));
  return out;
}

export function LeagueChat({ leagueId, isMember, onToast }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [wsLive, setWsLive] = useState(false);
  const lastSeen = useRef(null);
  const scrollRef = useRef(null);
  const pollRef = useRef(null);
  const wsRef = useRef(null);
  const inputRef = useRef(null);
  const [members, setMembers] = useState([]);
  // Mention autocomplete state
  const [mentionQuery, setMentionQuery] = useState(null);   // string after the open @, or null
  const [mentionSelected, setMentionSelected] = useState(0); // arrow-key index
  const [soundOn, setSoundOn] = useState(
    typeof window !== "undefined" && localStorage.getItem(MENTION_SOUND_PREF) !== "off"
  );

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, []);

  const appendMessage = useCallback((m) => {
    setMessages((prev) => {
      if (prev.some((p) => p.message_id === m.message_id)) return prev;
      return [...prev, m];
    });
    lastSeen.current = m.created_at;
    // Chime + bell-bump if this message mentions the viewer.
    const mentioned = user && (m.mentions || []).some((x) => x.user_id === user.user_id);
    if (mentioned && m.user_id !== user?.user_id) {
      playChime();
      try { window.dispatchEvent(new Event("lh:notification-bump")); } catch (_e) { /* noop */ }
    }
    setTimeout(scrollToBottom, 30);
  }, [scrollToBottom, user]);

  const refresh = useCallback(async (initial = false) => {
    try {
      const list = await fetchMessages(leagueId, initial ? null : lastSeen.current || null);
      if (list.length) {
        setMessages((prev) => {
          if (initial) return list;
          const seen = new Set(prev.map((m) => m.message_id));
          return [...prev, ...list.filter((m) => !seen.has(m.message_id))];
        });
        lastSeen.current = list[list.length - 1].created_at;
      } else if (initial) {
        setMessages([]);
      }
      if (initial) setLoaded(true);
      setTimeout(scrollToBottom, 30);
    } catch (e) {
      if (initial) {
        setLoaded(true);
        if (e?.response?.status !== 403) onToast?.("Could not load chat.");
      }
    }
  }, [leagueId, onToast, scrollToBottom]);

  // Initial REST load + WS bring-up. Polling is only the fallback —
  // once the socket is open we kill the timer and stay realtime.
  useEffect(() => {
    if (!isMember) return;
    let alive = true;
    refresh(true);
    // Lazy-load members for @mention autocomplete. Best-effort.
    fetchLeagueMembers(leagueId).then((rows) => {
      if (alive) setMembers(Array.isArray(rows) ? rows : []);
    }).catch(() => { /* non-blocking */ });

    const startPolling = () => {
      if (pollRef.current) return;
      pollRef.current = setInterval(() => refresh(false), POLL_MS);
    };
    const stopPolling = () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };

    let ws;
    try {
      ws = new WebSocket(buildWsUrl(leagueId));
      wsRef.current = ws;
    } catch {
      startPolling();
      return;
    }

    // Don't start polling until either:
    //   - the WS handshake fails / closes, OR
    //   - we time out waiting for the server's "ready" frame.
    // This avoids the duplicate REST fetch on every reconnect.
    const readyTimeout = setTimeout(() => {
      if (alive && !wsRef.current?._readyAcked) startPolling();
    }, 2500);

    ws.onopen = () => { /* wait for server "ready" — see ws.onmessage */ };
    ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        if (data.type === "ready") {
          if (alive) {
            ws._readyAcked = true;
            clearTimeout(readyTimeout);
            setWsLive(true);
            stopPolling(); // option A — replace polling once connected.
          }
        } else if (data.type === "message" && data.message) {
          appendMessage(data.message);
        } else if (data.type === "reaction" && data.message_id) {
          // Live reaction sync across all viewers of this league chat.
          setMessages((prev) => prev.map((m) =>
            m.message_id === data.message_id ? { ...m, reactions: data.reactions } : m
          ));
        }
      } catch { /* ignore malformed */ }
    };
    ws.onerror = () => { clearTimeout(readyTimeout); startPolling(); };
    ws.onclose = () => {
      if (!alive) return;
      clearTimeout(readyTimeout);
      setWsLive(false);
      startPolling(); // graceful fallback if the socket dies.
    };

    return () => {
      alive = false;
      clearTimeout(readyTimeout);
      stopPolling();
      try { ws?.close(); } catch { /* noop */ }
    };
  }, [leagueId, isMember, refresh, appendMessage]);

  // Mention autocomplete plumbing -------------------------------------------
  const detectMentionToken = (text, caret) => {
    // Walk back from the caret to the most recent '@'. If we find one,
    // and it's at a word boundary, treat what's after it as the query.
    const slice = text.slice(0, caret);
    const at = slice.lastIndexOf("@");
    if (at < 0) return null;
    // Must be at start or after whitespace
    if (at > 0 && !/\s/.test(slice[at - 1])) return null;
    const after = slice.slice(at + 1);
    if (/\s/.test(after)) return null; // closed by a space
    return after;
  };

  const onDraftChange = (e) => {
    const v = e.target.value.slice(0, 1000);
    setDraft(v);
    const caret = e.target.selectionStart ?? v.length;
    const q = detectMentionToken(v, caret);
    setMentionQuery(q);
    setMentionSelected(0);
  };

  const pickMention = (member) => {
    if (!inputRef.current) return;
    const el = inputRef.current;
    const caret = el.selectionStart ?? draft.length;
    const before = draft.slice(0, caret);
    const at = before.lastIndexOf("@");
    if (at < 0) return;
    const after = draft.slice(caret);
    const handle = (member.name || "").replace(/\s+/g, "");
    const next = `${draft.slice(0, at)}@${handle} ${after}`;
    setDraft(next);
    setMentionQuery(null);
    setTimeout(() => {
      el.focus();
      const pos = at + handle.length + 2;
      try { el.setSelectionRange(pos, pos); } catch (_e) { /* noop */ }
    }, 0);
  };

  const onKeyDown = (e) => {
    if (mentionQuery === null) return;
    const q = mentionQuery.toLowerCase();
    const matches = members
      .filter((m) => (m.name || "").toLowerCase().includes(q)
        || (m.name || "").toLowerCase().replace(/\s+/g, "").includes(q))
      .filter((m) => m.user_id !== user?.user_id)
      .slice(0, 6);
    if (matches.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setMentionSelected((i) => (i + 1) % matches.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setMentionSelected((i) => (i - 1 + matches.length) % matches.length);
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      pickMention(matches[mentionSelected] || matches[0]);
    } else if (e.key === "Escape") {
      setMentionQuery(null);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      // Prefer WS for sub-second delivery; fall back to REST.
      if (wsLive && wsRef.current?.readyState === 1) {
        wsRef.current.send(JSON.stringify({ body }));
        // Server will broadcast back; nothing to insert locally.
      } else {
        const sent = await sendMessage(leagueId, body);
        appendMessage(sent);
      }
      setDraft("");
    } catch (err) {
      onToast?.(err?.response?.data?.detail || "Send failed.");
    } finally { setSending(false); }
  };

  const toggleReaction = async (messageId, emoji) => {
    // Optimistic update for snappy feel — server will re-broadcast.
    setMessages((prev) => prev.map((m) => {
      if (m.message_id !== messageId) return m;
      const rxs = [...(m.reactions || [])];
      const idx = rxs.findIndex((r) => r.emoji === emoji);
      if (idx >= 0) {
        const r = rxs[idx];
        const mine = r.mine;
        const next = { ...r, mine: !mine, count: r.count + (mine ? -1 : 1) };
        if (next.count <= 0) rxs.splice(idx, 1);
        else rxs[idx] = next;
      } else {
        rxs.push({ emoji, count: 1, mine: true, user_ids: [user?.user_id] });
      }
      return { ...m, reactions: rxs };
    }));
    try {
      await reactToMessage(leagueId, messageId, emoji);
    } catch (err) {
      onToast?.(err?.response?.data?.detail || "Reaction failed.");
    }
  };

  if (!isMember) {
    return (
      <div className="glass rounded-2xl p-6 text-center" data-testid="chat-locked">
        <p className="text-sm text-white/60">🔒 Join the league to chat.</p>
      </div>
    );
  }

  return (
    <div data-testid="league-chat" className="flex flex-col gap-3" style={{ height: "min(560px, 70vh)" }}>
      <div className="flex items-center justify-between shrink-0">
        <p className="text-[10px] tracking-widest text-[#14b8a6]">💬 LEAGUE CHAT</p>
        <p
          data-testid="chat-status"
          className={`text-[10px] tracking-widest font-bold ${wsLive ? "text-[#14b8a6]" : "text-white/50"}`}
        >
          {wsLive ? "● LIVE" : "○ POLLING"}
        </p>
      </div>
      <div
        ref={scrollRef}
        data-testid="chat-message-list"
        className="glass rounded-2xl p-3 flex-1 min-h-0 overflow-y-auto space-y-2"
      >
        {!loaded ? (
          <div className="flex justify-center py-10"><div className="loader-ring" /></div>
        ) : messages.length === 0 ? (
          <p className="text-center text-sm text-white/50 py-10" data-testid="chat-empty">
            No messages yet — say hi 👋
          </p>
        ) : (
          messages.map((m) => {
            const mine = user && m.user_id === user.user_id;
            const ts = new Date(m.created_at);
            return (
              <div
                key={m.message_id}
                data-testid={`chat-msg-${m.message_id}`}
                className={`flex items-start gap-2 ${mine ? "justify-end" : "justify-start"}`}
              >
                {!mine && <AvatarChip avatarId={m.user_avatar_id} pfpUrl={m.user_pfp_url} size={32} />}
                <div className={`max-w-[75%] rounded-2xl px-3 py-2 ${mine ? "bg-[#14b8a6]/15 border border-[#14b8a6]/40" : "bg-white/5 border border-white/15"} group/msg relative`}>
                  <p className="text-[10px] tracking-widest text-[#14b8a6] flex items-center gap-2">
                    <span className="font-bold">{mine ? "YOU" : m.user_name}</span>
                    <span className="text-white/40">{ts.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                  </p>
                  <p className="text-sm text-white/90 whitespace-pre-wrap break-words mt-0.5">
                    {renderBody(m.body, m.mentions, user?.user_id)}
                  </p>
                  {/* Reaction chips */}
                  {(m.reactions || []).length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`chat-rx-row-${m.message_id}`}>
                      {m.reactions.map((r) => (
                        <button
                          type="button"
                          key={r.emoji}
                          onClick={() => toggleReaction(m.message_id, r.emoji)}
                          data-testid={`chat-rx-chip-${m.message_id}-${r.emoji}`}
                          className={`px-1.5 py-0.5 rounded-full text-[11px] flex items-center gap-1 border transition ${
                            r.mine
                              ? "bg-[#14b8a6]/20 border-[#14b8a6]/60 text-[#14b8a6]"
                              : "bg-black/40 border-white/15 text-white/80 hover:border-white/40"
                          }`}
                        >
                          <span>{r.emoji}</span>
                          <span className="tabular-nums">{r.count}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {/* Add-reaction picker — appears on hover/focus */}
                  <ReactionPicker
                    messageId={m.message_id}
                    onPick={(emoji) => toggleReaction(m.message_id, emoji)}
                    side={mine ? "left" : "right"}
                  />
                </div>
                {mine && <AvatarChip avatarId={user.avatar_id} pfpUrl={user.pfp_url} size={32} />}
              </div>
            );
          })
        )}
      </div>
      <form onSubmit={submit} className="relative shrink-0" data-testid="chat-form">
        {/* Autocomplete dropdown for @mentions */}
        {mentionQuery !== null && (() => {
          const q = mentionQuery.toLowerCase();
          const matches = members
            .filter((m) => (m.name || "").toLowerCase().includes(q)
              || (m.name || "").toLowerCase().replace(/\s+/g, "").includes(q))
            .filter((m) => m.user_id !== user?.user_id)
            .slice(0, 6);
          if (matches.length === 0) return null;
          return (
            <ul
              data-testid="mention-dropdown"
              className="absolute bottom-full left-0 right-0 mb-2 max-h-56 overflow-y-auto rounded-2xl border border-[#14b8a6]/30 bg-black/95 shadow-[0_8px_30px_rgba(0,0,0,0.6)] z-30"
            >
              {matches.map((m, idx) => (
                <li key={m.user_id}>
                  <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); pickMention(m); }}
                    data-testid={`mention-option-${m.user_id}`}
                    className={`w-full text-left flex items-center gap-2 px-3 py-2 hover:bg-[#14b8a6]/10 transition ${
                      idx === mentionSelected ? "bg-[#14b8a6]/15" : ""
                    }`}
                  >
                    <AvatarChip avatarId={m.avatar_id} pfpUrl={m.pfp_url} size={28} />
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">@{m.name}</p>
                      <p className="text-[10px] text-white/45">MMR {m.elo ?? "—"}</p>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          );
        })()}

        <div className="flex gap-2">
          <input
            ref={inputRef}
            value={draft}
            onChange={onDraftChange}
            onKeyDown={onKeyDown}
            placeholder="Say something… use @ to tag a teammate"
            maxLength={1000}
            data-testid="chat-input"
            className="flex-1 bg-black/40 border border-[#14b8a6]/30 rounded-2xl px-4 py-3 text-white"
          />
          <button
            type="button"
            onClick={() => {
              const next = !soundOn;
              setSoundOn(next);
              localStorage.setItem(MENTION_SOUND_PREF, next ? "on" : "off");
            }}
            data-testid="chat-sound-toggle"
            title={soundOn ? "Mute mention chime" : "Unmute mention chime"}
            aria-label={soundOn ? "Mute mention chime" : "Unmute mention chime"}
            className="w-12 rounded-2xl border border-white/15 hover:border-[#14b8a6]/40 text-white/70 hover:text-white transition-colors"
          >
            <i className={`fas ${soundOn ? "fa-volume-high" : "fa-volume-xmark"}`} aria-hidden="true" />
          </button>
          <button
            type="submit"
            disabled={sending || !draft.trim()}
            data-testid="chat-send"
            className="btn-neon rounded-2xl px-5 py-3 font-bold tracking-widest text-xs disabled:opacity-40"
          >
            {sending ? "..." : "SEND"}
          </button>
        </div>
      </form>
      <p className="text-[10px] text-white/40 text-right">{draft.length}/1000</p>
    </div>
  );
}

export default LeagueChat;

/**
 * ReactionPicker — small smiley button revealed on hover/focus of the
 * message bubble. Opens a 9-emoji palette; clicking an emoji toggles it.
 */
function ReactionPicker({ messageId, onPick, side }) {
  const [open, setOpen] = useState(false);
  const sideClass = side === "left" ? "-left-9" : "-right-9";
  return (
    <div className={`absolute top-1 ${sideClass} opacity-0 group-hover/msg:opacity-100 focus-within:opacity-100 transition-opacity`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`chat-rx-open-${messageId}`}
        title="Add reaction"
        className="w-7 h-7 rounded-full bg-black/70 border border-white/15 hover:border-[#14b8a6]/60 text-sm flex items-center justify-center"
      >
        <i className="far fa-smile text-white/70 text-xs" />
      </button>
      {open && (
        <div
          data-testid={`chat-rx-palette-${messageId}`}
          className="absolute top-8 z-30 grid grid-cols-3 gap-0.5 p-1 rounded-xl bg-black/95 border border-white/20 shadow-[0_8px_24px_rgba(0,0,0,0.6)]"
          onMouseLeave={() => setOpen(false)}
        >
          {REACTION_EMOJIS.map((e) => (
            <button
              type="button"
              key={e}
              data-testid={`chat-rx-pick-${messageId}-${e}`}
              onClick={() => { onPick(e); setOpen(false); }}
              className="w-7 h-7 rounded-md hover:bg-white/10 text-base"
            >
              {e}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
